import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import AlertTitle from '@mui/material/AlertTitle';
import IconButton from '@mui/material/IconButton';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import {
  dslHighlights,
  mergeHighlights,
  allHighlightTerms,
  columnSearchHighlights,
  serializeHighlightTerms,
} from 'src/utils/highlight-terms';

import { useTranslate } from 'src/locales';
import { BoolNode, SortOrder } from 'src/services/common.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { SortConfig } from 'src/services/api.types';
import { IOrgCandidateListItem } from 'src/services/candidates/candidates.types';
import { useUpdateCandidateRatingMutation } from 'src/services/candidates/candidates.service';

import Iconify from 'src/components/iconify';
import DataList from 'src/components/data-list/data-list';
import RecordPanel from 'src/components/record-panel/record-panel';
import { useRecordPanel } from 'src/components/record-panel/use-record-panel';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import { useColumnPreferences } from 'src/components/data-list/use-column-preferences';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';
import BulkAddCandidatesJobModal from 'src/sections/jobs/modals/bulk-add-candidate-job-modal';

import { buildCandidateColumns } from '../table/candidate-columns';
import { sortCandidateRows } from '../utils/sort-candidate-rows';
import StreamedSearchStatus from './streamed-search-status';
import { useCandidateSearchStream } from '../hooks/use-candidate-search-stream';

const PAGE_SIZE = 50;

interface AdvancedSearchResultsProps {
  /** The combined DSL (keyword + structured criteria). */
  searchDsl: BoolNode;
  /** True once a search has been run — the query is skipped until then. */
  hasSearched: boolean;
  /** When sourcing for a job, bulk "Add to job" targets this job. */
  jobId?: string;
}

/**
 * Advanced-search results on the shared DataList (JT1-1532, item 32): the same flat rows the
 * candidates list renders — configurable/sortable columns, per-column search, infinite scroll, and
 * the record panel via the leading eye (quick view) and the name (overlay). Selection drives bulk
 * Add to job / Add to list.
 *
 * The search is streamed (JT1-1802): matches appear as the server finds them, block by block, with
 * a progress bar until it has checked everyone or found 1,000. The one-shot search it replaced ran
 * past its 20s limit on the largest agencies and showed nothing at all. Every row is in hand once
 * found, so sorting and scrolling never go back to the server; a column search starts a new search.
 */
export default function AdvancedSearchResults({ searchDsl, hasSearched, jobId }: AdvancedSearchResultsProps) {
  const { t } = useTranslate();
  const tenant = useOrgTenant();
  const { openRecord } = useRecordPanel();

  const [visible, setVisible] = useState(PAGE_SIZE);
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'last_activity_at', sort_order: SortOrder.DESC });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const search = useCandidateSearchStream({ dsl: searchDsl, columnFilters: columnSearch, enabled: hasSearched });
  const { updateRow } = search;

  // a fresh search starts back at the top
  useEffect(() => { setVisible(PAGE_SIZE); }, [searchDsl, columnSearch]);

  /**
   * A failed search is NOT an empty result (JT1-1647): a 500 once rendered "0 candidates found"
   * with 418 matching candidates in the database. Rows found before a failure stay on screen,
   * under a warning that the list may be incomplete; with none, the list shows the error instead
   * of its empty state.
   */
  const searching = search.status === 'searching' || search.status === 'queued';
  const failed = search.status === 'error' && search.rows.length === 0;
  const partial = (search.status === 'error' && search.rows.length > 0) || (search.status === 'done' && search.partial);

  const sorted = useMemo(() => sortCandidateRows(search.rows, sortConfig), [search.rows, sortConfig]);
  const rows = useMemo(() => sorted.slice(0, visible), [sorted, visible]);
  const hasMore = visible < sorted.length;
  const found = search.rows.length;

  const onSortChange = useCallback((next: SortConfig) => setSortConfig(next), []);
  const onColumnSearchChange = useCallback((key: string, value: string) => {
    setColumnSearch((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
  }, []);
  const onLoadMore = useCallback(() => { if (hasMore) setVisible((count) => count + PAGE_SIZE); }, [hasMore]);

  const onSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const onSelectAll = useCallback((checked: boolean, ids: string[]) => setSelectedIds(checked ? Array.from(new Set(ids)) : []), []);

  const [updateRating] = useUpdateCandidateRatingMutation();
  const handleRatingUpdate = useCallback(async (candidateId: string, rating: number) => {
    const toastId = enqueueSnackbar(t('candidates.api.update_rating.loading'), { variant: 'loading', persist: true });
    try {
      await updateRating({ candidateId, rating }).unwrap();
      closeSnackbar(toastId);
      enqueueSnackbar(t('candidates.api.update_rating.success'), { variant: 'success' });
      updateRow(candidateId, { rating });
    } catch {
      closeSnackbar(toastId);
      enqueueSnackbar(t('candidates.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [t, updateRating, updateRow]);

  // Every searched term — the keyword, the criteria rows and the column searches — is highlighted in
  // the cells it could have matched, and rides onto opened records as `?q=` so their header, skills and
  // resume highlight it too (item 18, JT1-1693). Only a search that has run is highlighted.
  const highlights = useMemo(
    () => mergeHighlights(dslHighlights(hasSearched ? searchDsl : null), columnSearchHighlights(columnSearch)),
    [hasSearched, searchDsl, columnSearch]
  );
  const highlightQuery = useMemo(() => serializeHighlightTerms(allHighlightTerms(highlights)) || undefined, [highlights]);
  const handleQuickView = useCallback((id: string) => openRecord('candidate', id, { mode: 'quick', query: { q: highlightQuery } }), [openRecord, highlightQuery]);
  const handleOpenOverlay = useCallback((id: string) => openRecord('candidate', id, { mode: 'overlay', query: { q: highlightQuery } }), [openRecord, highlightQuery]);

  const registry = useMemo(
    () => buildCandidateColumns({ t, tenantType: tenant, onRatingUpdate: handleRatingUpdate, onOpenRecord: handleOpenOverlay, highlights }),
    [t, tenant, handleRatingUpdate, handleOpenOverlay, highlights]
  );
  const { columns, pickerModel, save } = useColumnPreferences('candidates', registry);

  const renderRowLeading = useCallback((row: IOrgCandidateListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row.id)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const addToJob = useBoolean();
  const addToList = useBoolean();
  const onSuccessAddToList = useCallback(() => { setSelectedIds([]); addToList.onFalse(); }, [addToList]);
  const hasSelection = selectedIds.length > 0;

  const toolbar = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
      <StreamedSearchStatus search={search} hidden={failed || !hasSearched} />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <DataListColumnPicker model={pickerModel} onSave={save} />
        <Button size="small" variant="soft" startIcon={<Iconify icon="mdi:invite" />} disabled={!hasSelection} onClick={addToJob.onTrue}>
          {t('common.add_to_job')}
        </Button>
        <Button size="small" variant="soft" startIcon={<Iconify icon="solar:list-bold" />} disabled={!hasSelection} onClick={addToList.onTrue}>
          {t('common.add_to_list', { defaultValue: 'Add to list' })}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <>
      {partial && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t('candidates.search.partial')}</AlertTitle>
          {t('candidates.search.partial_hint')}
        </Alert>
      )}

      <Card sx={{ borderRadius: 1 }}>
        <DataList<IOrgCandidateListItem>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          // placeholder rows until the first match arrives; after that the progress bar carries it
          isSearching={searching && found === 0}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          sort={sortConfig}
          onSortChange={onSortChange}
          columnSearch={columnSearch}
          onColumnSearchChange={onColumnSearchChange}
          toolbar={toolbar}
          selectable
          selectedIds={selectedIds}
          onSelectRow={onSelectRow}
          onSelectAll={onSelectAll}
          renderRowLeading={renderRowLeading}
          emptyLabel={hasSearched ? t('candidates.search.no_results') : t('candidates.search.initial_message')}
          errorContent={failed ? (
            <Alert severity="error" sx={{ my: 2 }}>
              <AlertTitle>{t('candidates.search.failed')}</AlertTitle>
              {t('candidates.search.failed_hint')}
            </Alert>
          ) : undefined}
        />
      </Card>

      <RecordPanel type="candidate" ids={rows.map((r) => r.id)} total={found} jobId={jobId} />

      <BulkAddCandidatesJobModal open={addToJob.value} onClose={addToJob.onFalse} candidates={selectedIds} jobId={jobId} />
      <AddItemsModal open={addToList.value} onClose={addToList.onFalse} onSuccess={onSuccessAddToList} items={selectedIds} entityType={ListEntityType.CANDIDATE} />
    </>
  );
}
